import React, { FC, ReactNode, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Resizable } from 'components/atoms/resizable';
import { FilterBar } from 'components/molecules/filter-bar';
import { Page } from 'components/molecules/page';
import {
  StyledPage,
  Content,
  ContentWrapper,
  Layout,
  Progress,
  Sider,
  BannerWrapper,
} from './components';

export interface ArgonProps {
  documentTitle?: string;
  title: ReactNode;
  peel?: ReactNode;
  tabs?: ReactNode;
  sider?: ReactNode;
  breadcrumbs?: ReactNode;
  actions: {
    primary?: ReactNode;
    secondary?: ReactNode;
    central?: ReactNode;
  };
  progress?: number;
  content: ReactNode;
  extraContent?: ReactNode;
  className?: string;
  isDistractionFree?: boolean;
  whiteHeader?: boolean;
  siderResizable?: boolean;
  siderWidth?: number;
  siderOffset?: number;
  siderMinWidth?: number;
  siderMaxWidth?: number;
  onResize?: (width: number) => void;
  onResizeStart?: (width: number) => void;
  onResizeStop?: (width: number) => void;
  banner?: ReactNode;
}

export const Argon: FC<ArgonProps> = ({
  documentTitle,
  title,
  peel,
  tabs,
  sider,
  breadcrumbs,
  progress,
  content,
  extraContent,
  actions: { primary, secondary, central },
  className,
  isDistractionFree,
  whiteHeader,
  siderResizable,
  siderWidth,
  siderOffset,
  siderMaxWidth,
  siderMinWidth,
  onResize,
  onResizeStart,
  onResizeStop,
  banner,
}) => {
  const [width, setWidth] = useState(siderWidth ?? 256);

  return (
    <StyledPage className={className}>
      {peel != null ? <Page.Peel>{peel}</Page.Peel> : null}
      <Page.Header
        $white={whiteHeader}
        title={title}
        breadcrumbs={breadcrumbs}
        tabs={tabs}
      />
      <Helmet>
        <title>{documentTitle}</title>
      </Helmet>
      <Page.Content>
        <Layout>
          {sider != null ? (
            <Resizable
              initialWidth={width}
              minWidth={siderMinWidth ?? 256}
              maxWidth={siderMaxWidth ?? 600}
              onChange={(w) => {
                setWidth(w);
                onResize?.(w);
              }}
              offset={siderOffset ?? 0}
              disabled={!siderResizable}
              hideContent={isDistractionFree}
              onResizeStart={onResizeStart}
              onResizeStop={onResizeStop}
            >
              <Sider width={isDistractionFree ? 0 : width}>{sider}</Sider>
            </Resizable>
          ) : null}
          <Content>
            {banner != null ? (
              <BannerWrapper id="banner_wrapper">{banner}</BannerWrapper>
            ) : null}
            {primary ?? secondary ?? central != null ? (
              <FilterBar>
                {primary != null ? (
                  <FilterBar.Primary>{primary}</FilterBar.Primary>
                ) : null}
                {central != null ? (
                  <FilterBar.Central>{central}</FilterBar.Central>
                ) : null}
                {secondary != null ? (
                  <FilterBar.Secondary>{secondary}</FilterBar.Secondary>
                ) : null}
              </FilterBar>
            ) : null}
            <ContentWrapper $full={(primary ?? secondary ?? central) == null}>
              <Progress progress={progress} />
              {content}
            </ContentWrapper>
            {extraContent}
          </Content>
        </Layout>
      </Page.Content>
    </StyledPage>
  );
};
